import type { SizeName } from 'src/constants/image-display-sizes'

type Maybe<T> = T | null

export const IMAGE_BREAKPOINTS = ['base', '2xs', 'md', 'lg'] as const

export type ResponsiveImageProp<TValue> = {
    base: TValue
    '2xs'?: TValue
    md?: TValue
    lg?: TValue
}

export type ResolveImageUrlArgs = {
    imageFilePath: string
    imageResizerBaseUrl?: string
    dimensions: ImageDimensions
    crop?: boolean
}

export type ImageBreakpoint = (typeof IMAGE_BREAKPOINTS)[number]

// This corresponds to the type returned by GraphQL
export interface ImageEntity {
    baseImageUrl: string
    alt: Maybe<string>
    title?: Maybe<string>
}

/**
 * Enum of our standard aspect ratios
 *
 * !!IMPORTANT!!
 * If any more aspect ratios are needed, they must be added to `tailwind-safelist.md` as well.
 */
export const AspectRatioEnum = {
    '1/1': '1/1',
    '4/3': '4/3',
    '3/4': '3/4',
    '5/4': '5/4',
} as const

export type AspectRatio = keyof typeof AspectRatioEnum

export type ImageSizeSpec = {
    aspectRatio?: AspectRatio
    size: SizeName
    /**
     * If you set this to true, and the original image doesn't match the requested aspect
     * ratio, then the image will be cropped to fit it.
     * Defaults to false. As long as `objectFit` is set to any value other than `cover`
     * or `fill`, the aspect ratio of the original image will be maintained, while
     * staying contained within the display dimensions..
     */
    crop?: boolean
}

export type ImageDimensions = Readonly<{ width: number; height: number }>

// export type ImageSize = Readonly<{
//     [sizeName: string]: ImageDimensions
// }>

export type ImageSizesForEachAspectRatio = Readonly<
    Record<AspectRatio, ImageDimensions>
>

export type ImageSizeData = Readonly<
    Record<SizeName, ImageSizesForEachAspectRatio>
>

export interface ImageSizeMapInterface
    extends Map<SizeName, ImageSizesForEachAspectRatio> {}
